import React, { useRef, useState, useEffect, useCallback } from "react"
import { useFullscreen, useToggle } from "react-use"
import { MyGantt } from "../../../components"
import { DateTime } from "luxon"
import {
    FormField,
    Select,
    Button,
    Tab,
    Box,
    SelectMultiple,
} from "grommet"

const colors = {
    tasks: "#14bd60",
    projects: "#02a2dd",
    leaves: "#ffca58",
    holidays: "purple",
}

const defaultEventsFilters = {
    type: [],
    year: "now",
    viewMode: "Day",
    dateRange: {
        start: DateTime.now().startOf("day"),
        end: DateTime.now().plus({ year: 1 }),
    },
}

export default function GanttTab({ events, rescheduleMutations }) {
    const [show, toggle] = useToggle(false)
    const fullscreenRef = useRef(null)
    const [tasks, setTasks] = useState({ tasks: [], date: Date.now() })
    const isFullscreen = useFullscreen(fullscreenRef, show, {
        onClose: () => toggle(false),
    })
    const handleExpanderClick = useCallback(task => {
        setTasks(tasks => ({
            ...tasks,
            tasks: tasks.tasks.map(t => (t.id === task.id ? task : t)),
        }))
    }, [])
    const [eventFilters, setEventFilters] = useState({
        ...defaultEventsFilters,
    })

    const handleEventsSelectChange = useCallback(
        ({ value }) => {
            let start
            let end = DateTime.now()

            switch (value) {
                case "now":
                    start = DateTime.now().startOf("day")
                    end = DateTime.now().plus({ year: 1 })
                    break

                case "last_year":
                    start = DateTime.now().minus({ year: 1 }).startOf("year")
                    end = DateTime.now().minus({ year: 1 }).endOf("year")
                    break

                case "next_year":
                    start = DateTime.now().plus({ year: 1 }).startOf("year")
                    end = DateTime.now().plus({ year: 1 }).endOf("year")
                    break

                case "this_year":
                default:
                    start = DateTime.now().startOf("year")
                    end = DateTime.now().endOf("year")
                    break
            }

            setEventFilters(filters => ({
                ...filters,
                year: value,
                dateRange: { start, end },
            }))
        },
        [setEventFilters]
    )

    useEffect(() => {
        if (!Array.isArray(events)) {
            return { tasks: [], date: Date.now() }
        }

        let filtered = [...events]

        if (eventFilters.type.length > 0) {
            filtered = filtered.filter(event => {
                if (eventFilters.type.includes("projects")) {
                    return (
                        eventFilters.type.includes(event.type) ||
                        eventFilters.type.includes(event.subtype)
                    )
                }

                if (event.type === "leaves" && eventFilters.type.includes("leaves")) {
                    return event.type === "leaves" && event.subtype !== "holiday"
                }

                if (event.type === "leaves" && eventFilters.type.includes("holiday")) {
                    return event.subtype === "holiday"
                }

                return eventFilters.type.includes(event.type)
            })
        }

        if (eventFilters?.dateRange?.end && eventFilters?.dateRange?.start) {
            const { start, end } = eventFilters.dateRange

            filtered = filtered.filter(event => {
                let eventStart = DateTime.fromJSDate(event.starts_at)
                let eventEnd = DateTime.fromJSDate(event.ends_at)

                if (event.subtype === "projects") {
                    const project = events.find(
                        e => e.id === event.taskable_id && e.type === "projects"
                    )

                    if (project) {
                        eventStart = DateTime.fromJSDate(project.starts_at)
                        eventEnd = DateTime.fromJSDate(project.ends_at)
                    }
                }

                const startIsInRange = start <= eventStart && eventStart <= end
                const endIsInRange = start <= eventEnd && eventEnd <= end

                return startIsInRange || endIsInRange
            })
        }

        let projectDisplayOrderIndex = []

        filtered = filtered
            .map(event => {
                const ganttEvent = {
                    event,
                    start: event.starts_at,
                    end: event.ends_at,
                    name: event.titleGantt ?? event.title,
                    id: event.id + "_" + event.type,
                    type: event.type === "projects" ? "project" : "task",
                    laravelType: event.type,
                    progress: 0,
                    isDisabled: event.isDisabled ?? false,
                    styles: {
                        backgroundColor: colors[event.type],
                        backgroundSelectedColor: "#888",
                    },
                }

                if (event.taskable_type && event.taskable_type === "project") {
                    ganttEvent.project = event.taskable_id + "_projects"
                }

                if (event.type === "projects") {
                    ganttEvent.hideChildren = filtered.some(
                        e => e.taskable_id === event.id && e.taskable_type === "project"
                    )
                        ? true
                        : undefined
                }

                return ganttEvent
            })
            .map((event, i) => {
                event.displayOrder = (i + 1) * 100
                if (event.type === "project") {
                    projectDisplayOrderIndex[event.id] = event.displayOrder
                }
                return event
            })
            .map((event, i) => {
                if (projectDisplayOrderIndex[event.project]) {
                    event.displayOrder =
                        parseInt(projectDisplayOrderIndex[event.project]) + i + 1
                }
                return event
            })

        const date = ["this_year", "now"].includes(eventFilters.year)
            ? Date.now()
            : filtered[0]?.start
                ? DateTime.fromJSDate(filtered[0].start).toMillis()
                : null

        setTasks({ tasks: filtered, date })
    }, [events, eventFilters])

    return (
        <Tab title="Planification">
            <Box
                pad={isFullscreen ? "medium" : { vertical: "medium" }}
                ref={fullscreenRef}
                style={{
                    backgroundColor: "#fff",
                    overflow: isFullscreen ? "auto" : null,
                }}
            >
                <Box
                    direction="row"
                    align="start"
                    wrap
                    gap="small"
                    height={isFullscreen ? { min: "94px" } : null}
                >
                    <FormField label="Période">
                        <Select
                            dropProps={{ inline: true }}
                            onChange={handleEventsSelectChange}
                            labelKey="label"
                            defaultValue="now"
                            valueKey={{
                                key: "value",
                                reduce: true,
                            }}
                            options={[
                                {
                                    value: "now",
                                    label: "Maintenant + 365j",
                                },
                                {
                                    value: "last_year",
                                    label: "L'année passée",
                                },
                                {
                                    value: "this_year",
                                    label: "Cette année",
                                },
                                {
                                    value: "next_year",
                                    label: "L'année prochaine",
                                },
                            ]}
                        />
                    </FormField>
                    <FormField label="Type">
                        <SelectMultiple
                            dropProps={{ inline: true }}
                            onChange={({ value }) => {
                                setEventFilters(filters => ({
                                    ...filters,
                                    type: value,
                                }))
                            }}
                            multiple
                            labelKey="label"
                            placeholder="Choisir un type"
                            valueKey={{
                                key: "value",
                                reduce: true,
                            }}
                            options={[
                                {
                                    value: "tasks",
                                    label: "Tâches",
                                },
                                {
                                    value: "projects",
                                    label: "Projets",
                                },
                                {
                                    value: "leaves",
                                    label: "Absences",
                                },
                                {
                                    value: "holiday",
                                    label: "Vacances",
                                },
                            ]}
                        />
                    </FormField>
                    <FormField label="Vue">
                        <Select
                            dropProps={{ inline: true }}
                            onChange={({ value }) => {
                                setEventFilters(filters => ({
                                    ...filters,
                                    viewMode: value,
                                }))
                            }}
                            labelKey="label"
                            defaultValue="Day"
                            valueKey={{
                                key: "value",
                                reduce: true,
                            }}
                            options={[
                                {
                                    value: "Hour",
                                    label: "Heure",
                                },
                                {
                                    value: "Day",
                                    label: "Jour",
                                },
                                {
                                    value: "Week",
                                    label: "Semaine",
                                },
                                {
                                    value: "Month",
                                    label: "Mois",
                                },
                            ]}
                        />
                    </FormField>
                    <Box margin={{ bottom: "small", left: "auto" }}>
                        <Button
                            onClick={() => {
                                toggle()
                            }}
                            label={isFullscreen ? "sortir" : "plein écran"}
                        />
                    </Box>
                </Box>
                <MyGantt
                    viewDate={tasks.date}
                    mutations={rescheduleMutations}
                    events={tasks.tasks}
                    handleExpanderClick={handleExpanderClick}
                    isFullscreen={isFullscreen}
                />
            </Box>
        </Tab>
    )
}